/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import { S3_IMAGES, TERMINAL_BASE } from 'Constants/General';
import Alert from 'Components/Alert';
import Modal from 'Components/Modal';
import CourseVigencyEditor from 'Components/CourseVigencyEditor';
import {
  Button,
  NumberField,
  SelectField,
  SettingsRow,
  SettingsSection,
  TextArea,
  TextField,
  uiTheme,
} from 'Components/ui';
import FormValidations from 'Utils/FormValidations';
import { validateVigency } from 'Utils/courseVigency';
import { s3 } from 'Services/AWS/s3';
import ThumbnailField from './Components/ThumbnailField';
import SaveInfoModal from './Components/SaveInfoModal';
import useFetch from './Hooks/useFetch';
import useModal from './Hooks/useModal';

const Page = styled.div({
  background: uiTheme.color.pageBg,
  padding: 'clamp(1.5rem, 4vw, 2.75rem) clamp(1rem, 4vw, 2.5rem)',
  fontFamily: uiTheme.font,
});

const PageHead = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '1rem',
  width: '100%',
  maxWidth: '62.5rem',
  margin: '0 auto 1.5rem',
});

const PageTitle = styled.h1({
  margin: 0,
  fontFamily: uiTheme.font,
  fontSize: 'clamp(1.5rem, 2vw, 1.875rem)',
  lineHeight: 1.15,
  color: uiTheme.color.titleStrong,
});

const PageSubtitle = styled.p({
  margin: '0.5rem 0 0',
  maxWidth: '48rem',
  fontFamily: uiTheme.font,
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  color: uiTheme.color.text,
});

const Asterisk = styled.span({
  color: uiTheme.color.danger,
});

const Actions = styled.div({
  display: 'flex',
  gap: '0.75rem',
  flexShrink: 0,
  flexWrap: 'wrap',
});

const Sections = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  width: '100%',
  maxWidth: '62.5rem',
  marginInline: 'auto',
});

const TrainingFields = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  '& > *': {
    flex: '1 1 7rem',
    minWidth: 0,
  },
});

const Loading = styled.div({
  width: '100%',
  maxWidth: '62.5rem',
  margin: '0 auto',
  fontFamily: uiTheme.font,
  color: uiTheme.color.text,
});

const splitTrainingHours = trainingHours => {
  const totalHours = parseFloat(trainingHours) || 0;
  const days = Math.floor(totalHours / 8);
  const remainingHours = totalHours % 8;
  const hours = Math.floor(remainingHours);
  const minutes = Math.round((remainingHours - hours) * 60);

  return {
    days,
    hours,
    minutes,
  };
};

const unitSuffix = (value, one, many) => (value === 1 ? one : many);

export default function EditCourseInformation({ handleNameChange }) {
  const { t2 } = useTranslation('EditCourse');
  const { courseId } = useParams();
  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [unchanged, setUnchanged] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState();
  const [vigencyErrors, setVigencyErrors] = useState([]);
  const [formData, setFormData] = useState({});
  const { openModal, setOpenModal } = useModal();

  const { course, categories, priorities, setCourseBasicInformation } = useFetch(courseId);
  const [days, setDays] = useState(null);
  const [hours, setHours] = useState(null);
  const [minutes, setMinutes] = useState(null);

  useEffect(() => {
    if (isLoading && course.name) {
      const duration = splitTrainingHours(course.trainingHours);

      setFormData({ ...course });
      setDays(duration.days);
      setHours(duration.hours);
      setMinutes(duration.minutes);
      setIsLoading(false);
    }
  }, [course, isLoading]);

  const update = patch => {
    setUnchanged(false);
    setFormData(previous => ({ ...previous, ...patch }));
  };

  const updateDays = value => {
    if (value === null || value >= 0) {
      setUnchanged(false);
      setDays(value);
    }
  };

  const updateHours = value => {
    if (value === null || (value >= 0 && value <= 99)) {
      setUnchanged(false);
      setHours(value);
    }
  };

  const updateMinutes = value => {
    if (value === null || (value >= 0 && value <= 59)) {
      setUnchanged(false);
      setMinutes(value);
    }
  };

  const handleDiscard = () => {
    const duration = splitTrainingHours(course.trainingHours);

    setFormData({ ...course });
    setDays(duration.days);
    setHours(duration.hours);
    setMinutes(duration.minutes);
    setVigencyErrors([]);
    setErrors(undefined);
    setUnchanged(true);
  };

  const handleImageUpload = file => {
    const extension = file.name.split('.').slice(-1)[0];
    update({
      thumbnail: {
        file,
        extension,
      },
    });
  };

  const handleSubmit = async () => {
    let key = formData.thumbnail;
    const { file, extension } = formData.thumbnail || {};
    const isFileUploaded = file !== undefined && extension !== undefined;

    if (isFileUploaded) {
      try {
        key = `${S3_IMAGES}/${formData.id}/CourseImage.${extension}`;
        await s3.uploadFileToBucketWithKeyProvided(key, file, file.type || 'image/jpeg');
      } catch (caught) {
        console.error(`Error ${caught}`);
        setSaveError(true);
        setOpenModal(false);
        return false;
      }
    }

    const totalHours = (days || 0) * 8 + (hours || 0) + (minutes || 0) / 60;

    const response = await setCourseBasicInformation({
      ...formData,
      thumbnail: key,
      trainingHours: totalHours,
      terminals: [TERMINAL_BASE],
    });

    // La mutación devuelve el error en vez de lanzarlo (Course_SetBasicInformation
    // termina en `.catch(e => e)`), así que sin `id` el guardado no ocurrió y hay
    // que decirlo: cerrar el modal en silencio se lee como éxito.
    if (!response?.id) {
      console.error('No se pudo guardar la información básica del curso:', response);
      setSaveError(true);
      setOpenModal(false);
      return false;
    }

    handleNameChange(formData.name);
    setOpen(true);
    setUnchanged(true);
    setOpenModal(false);
    return true;
  };

  const validate = () => {
    const temp = {};
    const nextVigencyErrors = validateVigency({
      expirationRule: formData.expirationRule ?? null,
      dueAmount: formData.dueAmount ?? null,
      dueUnit: formData.dueUnit ?? null,
    });

    temp.courseName = formData?.name?.length >= 3 ? '' : t2('FormCourseNameError');
    temp.category = formData.category ? '' : t2('FormCourseCategoryError');
    temp.priority = formData.priority ? '' : t2('FormCoursePriorityError');
    temp.description = FormValidations.courseDescription(formData?.description);

    setErrors({ ...temp });
    setVigencyErrors(nextVigencyErrors);

    return Object.values(temp).every(value => value === '') && nextVigencyErrors.length === 0;
  };

  const submitHandler = event => {
    event.preventDefault();

    if (validate()) {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  if (isLoading) {
    return (
      <Page>
        <Loading>{t2('Loading')}</Loading>
      </Page>
    );
  }

  return (
    <Page>
      <PageHead>
        <div>
          <PageTitle>Información Básica</PageTitle>
          <PageSubtitle>
            Revisa y actualiza los datos del curso. Los campos con <Asterisk>*</Asterisk> son obligatorios para
            publicar.
          </PageSubtitle>
        </div>
        <Actions>
          <Button type="button" variant="secondary" onClick={handleDiscard}>
            Descartar
          </Button>
          <Button type="submit" form="basic-info" disabled={unchanged}>
            Guardar
          </Button>
        </Actions>
      </PageHead>

      <form id="basic-info" onSubmit={submitHandler}>
        <Sections>
          <SettingsSection title="Contenido">
            <SettingsRow
              label="Nombre de curso"
              help="Aparece en el catálogo y en los reportes."
              required
              htmlFor="nombre"
            >
              <TextField
                id="nombre"
                value={formData.name || ''}
                error={errors?.courseName}
                onChange={name => update({ name })}
              />
            </SettingsRow>

            <SettingsRow label="Categoría" help="Define en qué grupo se lista el curso." required htmlFor="categoria">
              <SelectField
                id="categoria"
                value={formData.category || ''}
                options={categories.map(item => ({ value: item.id, label: item.name }))}
                placeholder="Elige una categoría"
                error={errors?.category}
                onChange={category => update({ category })}
              />
            </SettingsRow>

            <SettingsRow
              label="Descripción"
              help="Máximo 255 caracteres. Explica qué se aprende."
              htmlFor="descripcion"
              align="start"
            >
              <TextArea
                id="descripcion"
                value={formData.description || ''}
                maxLength={255}
                error={errors?.description}
                onChange={description => update({ description })}
              />
            </SettingsRow>
          </SettingsSection>

          <SettingsSection title="Disponibilidad">
            <SettingsRow
              label="Prioridad de curso"
              help="Clasifica el curso en el catálogo y en los reportes."
              required
              htmlFor="prioridad"
            >
              <SelectField
                id="prioridad"
                value={formData.priority || ''}
                options={priorities.map(item => ({ value: item.id, label: item.name }))}
                placeholder="Elige una prioridad"
                error={errors?.priority}
                onChange={priority => update({ priority })}
              />
            </SettingsRow>

            <CourseVigencyEditor
              value={{
                expirationRule: formData.expirationRule ?? null,
                dueAmount: formData.dueAmount ?? null,
                dueUnit: formData.dueUnit ?? null,
              }}
              errors={vigencyErrors}
              onChange={update}
            />

            <SettingsRow
              label="Tiempo de entrenamiento"
              help="Duración estimada del curso. Un día equivale a 8 horas: los reportes siempre lo muestran en horas."
              align="start"
            >
              <TrainingFields>
                <NumberField
                  id="dias"
                  aria-label="Días"
                  value={days}
                  min={0}
                  onChange={updateDays}
                  suffix={unitSuffix(days, 'día', 'días')}
                />
                <NumberField
                  id="horas"
                  aria-label="Horas"
                  value={hours}
                  min={0}
                  max={99}
                  onChange={updateHours}
                  suffix={unitSuffix(hours, 'hora', 'horas')}
                />
                <NumberField
                  id="minutos"
                  aria-label="Minutos"
                  value={minutes}
                  min={0}
                  max={59}
                  onChange={updateMinutes}
                  suffix={unitSuffix(minutes, 'minuto', 'minutos')}
                />
              </TrainingFields>
            </SettingsRow>
          </SettingsSection>

          <SettingsSection title="Presentación">
            <SettingsRow
              label="Imagen de portada"
              help="Se muestra en el catálogo y en la tarjeta del curso."
              align="start"
            >
              <ThumbnailField
                value={formData.thumbnail}
                onFile={handleImageUpload}
                courseName={formData.name}
                description={formData.description}
                categoryName={categories.find(item => item.id === formData.category)?.name}
              />
            </SettingsRow>
          </SettingsSection>
        </Sections>
      </form>

      <Modal
        open={openModal}
        setOpen={setOpenModal}
        event={handleClose}
        Children={
          <SaveInfoModal
            accept={handleSubmit}
            cancel={handleClose}
            title={t2('BasicInfoModalTitle')}
            message={t2('BasicInfoModalMessage')}
          />
        }
      />
      <Alert message={t2('BasicInfoAlertSuccess')} open={open} setOpen={setOpen} />
      <Alert message={t2('BasicInfoAlertError')} open={saveError} setOpen={setSaveError} error />
    </Page>
  );
}
